/**
 * <ImpersonationBanner> — « Tu agis en tant que … » : l'admin qui voit l'app avec les yeux d'une
 * famille ou d'un tuteur, et la porte pour en sortir (molecule).
 *
 * Une barre pleine largeur en haut de chaque espace, aux tons de l'alerte (ceux de
 * `<LiveAppBanner>`) : la session n'est pas celle de l'admin, et rien ne doit le faire oublier.
 * « Déconnecter » est la seule sortie : elle révoque la session et rend l'admin à ses comptes. Le
 * bouton garde sa roue pendant la sortie — c'est une action (§ « Chargements »).
 *
 * Contrôlée : le nom et la sortie en cours arrivent, le geste remonte.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"

export type ImpersonationBannerProps = {
    /** Le compte vu : prénom et nom. Vide → « ce compte ». */
    name: string
    /** La sortie est partie : le bouton tourne et ne se presse plus. */
    exiting?: boolean
    /** « Déconnecter » : sortir de la session impersonnée. */
    onExit: () => void
    testID?: string
}

const EXIT = "Déconnecter"

/** L'encre de l'alerte, sur sa tinte. */
const INK = "warning-strong"

export function ImpersonationBanner(props: ImpersonationBannerProps) {
    const { name, exiting = false, onExit, testID } = props

    return (
        <View style={styles.bar} accessibilityRole="alert" testID={testID}>
            <Row gap="2" align="center">
                <Icon name="eye" size={16} color={INK} />
                <Text size="text-sm" weight="semibold" color={INK} style={styles.message}>
                    {`Tu agis en tant que ${name.trim() || "ce compte"}`}
                </Text>
                <Button
                    label={EXIT}
                    size="sm"
                    variant="ghost"
                    color={INK}
                    iconName="door-open"
                    onPress={onExit}
                    loading={exiting}
                    disabled={exiting}
                />
            </Row>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    bar: {
        backgroundColor: theme.colors.warningLight,
        paddingHorizontal: theme.spacing["4"],
        paddingVertical: theme.spacing["2"],
    },
    message: { flex: 1, flexShrink: 1 },
}))
